import React, { useEffect, useMemo, useState } from "react";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import icons from "../../../assets/images/constantIcons";

const convertTo12HourFormat = (datetimeStr) => {
  if (!datetimeStr) return "-";
  const date = new Date(datetimeStr);
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const useAttendanceHistory = (staffId) => {
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [rowsPerPage, setRowsPerPage] = useState(15); // Track rows per page
  const [attendaceHistory, setAttendaceHistory] = useState([]);
  const [singleAttendaceHistory, setSingleAttendaceHistory] = useState([]);
  const [isViewOpen, setIsViewOpen] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // fetch attendace history data
  const fetchAttendanceHis = async () => {
    try {
      const response = await apiService.get(
        `/attendance_histories/show_staff_attendance?staff_user_id=${staffId}`
      );
      setAttendaceHistory(response.daata);
    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    fetchAttendanceHis();
  }, []);

  const handleView = async (id) => {
    setIsLoading(true);
    try {
      await fetchSingleAttendanceHis(id);
      setIsViewOpen(id);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSingleAttendanceHis = async (id) => {
    console.log("id", id);
    try {
      const response = await apiService.get(`/attendance_histories/${id}`);
      console.log(response.data);
      setSingleAttendaceHistory(response.data);
    } catch (errors) {
      handleError(errors);
    }
  };

  const attendanceDetails = [
    { label: "Date", value: singleAttendaceHistory?.date },
    {
      label: "Punch In",
      value: singleAttendaceHistory?.punch_logs?.map((value, index) => (
        <span className="flex flex-col" key={index}>
          {convertTo12HourFormat(value.punch_in)}
        </span>
      )),
    },
    {
      label: "Punch Out",
      value: singleAttendaceHistory?.punch_logs?.map((value, index) => (
        <span className="flex flex-col" key={index}>
          {convertTo12HourFormat(value.punch_out)}
        </span>
      )),
    },
    {
      label: "Productivity",
      value: singleAttendaceHistory?.punch_logs?.map((value, index) => (
        <span className="flex flex-col" key={index}>
          {value.productivity}
        </span>
      )),
    },
  ];

  // Define the columns
  const columns = useMemo(
    () => [
      { Header: "SR.NO", accessor: "srNo" },
      { Header: "DATE", accessor: "date" },
      {
        Header: "PUNCH IN",
        accessor: "check_in",
        disableSortBy: true,
        Cell: ({ value }) => <span>{value ? apiService.convert24To12Hr(value) : "-"}</span>,
      },
      {
        Header: "PUNCH OUT",
        accessor: "check_out",
        disableSortBy: true,
        Cell: ({ value }) => <span>{value ? apiService.convert24To12Hr(value) : "-"}</span>,
      },
      { Header: "PRODUCTION", accessor: "production", disableSortBy: true },
      {
        Header: "status",
        accessor: "status",
        Cell: ({ value }) => {
          return (
            <div className="flex justify-start md:justify-center items-center">
              <p
                className={`text-xs sm:text-sm font-medium flex justify-center items-center rounded-3xl w-[100px] md:w-auto px-4 py-[6px]  ${
                  value === "Present"
                    ? "text-textblue bg-textblue bg-opacity-[10%]"
                    : value === "Half Day"
                    ? "text-yellow bg-yellow bg-opacity-[10%]"
                    : "text-red bg-red bg-opacity-[10%]"
                }`}
              >
                {value}
              </p>
            </div>
          );
        },
      },
      {
        Header: "action",
        accessor: "action",
        Cell: ({ row }) => {
          return (
            <div className="flex justify-start md:justify-center">
              <div
                onClick={() => handleView(row.original.id)}
                className="p-2 bg-green50 text-green rounded-full w-[40px] h-[40px] cursor-pointer flex items-center justify-center"
              >
                <icons.IoEyeOutline className="text-lg font-bold" />
              </div>
              {isViewOpen && (
                <>
                  <div className="fixed inset-0 bg-black bg-opacity-10 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg p-6 w-[90%] max-w-lg h-auto overflow-y-auto relative shadow-lg">
                      {/* Close Button */}
                      <div
                        className="absolute top-4 right-4 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px] flex justify-center items-center"
                        onClick={() => setSingleAttendaceHistory(false)}
                      >
                        <icons.IoClose className="text-green700 text-xl" />
                      </div>
                      <h2 className="text-xl font-semibold mb-4 text-gray-700 text-start">
                        Attendance Details
                      </h2>

                      <div className="overflow-x-auto">
                        <table className="category-details-table w-full border-collapse border border-gray-300">
                          <thead className="">
                            <tr className="bg-green700 text-white">
                              <th className="border p-2  w-1/2">Field</th>
                              <th className="border p-2  w-1/2">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attendanceDetails?.map((value, index) => (
                              <tr key={index}>
                                <td className="border p-2">{value.label}</td>
                                <td className="border p-2">{value.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end space-x-4 pt-6">
                        <button
                          type="button"
                          onClick={() => handleView(null)}
                          className="py-2 px-4 text-sm bg-white text-green rounded-lg border border-green"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [isViewOpen]
  );

  // Sample data
  const data = useMemo(() => {
    if (!Array.isArray(attendaceHistory)) return [];
    return attendaceHistory?.map((data, index) => ({
      srNo: index + 1,
      id: data.id,
      date: data.date,
      check_in: data.check_in,
      check_out: data.check_out ,
      punch_logs: data.punch_logs,
      punch_out: data.punch_logs,
      productivity: data.punch_logs,
      production: data.production || "N/A",
      status: data.status === "present" ? "Present" : "Absent",
    }));
  }, [attendaceHistory]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

export default useAttendanceHistory;
